import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';
import { type Filter } from '../..';
import DashboardBox, { ALLOW_ALL } from '../../components/DashboardBox';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { PaidImmunizationDetail } from 'generated/api';
import { api } from 'careand-redux/api/ApiThunks';
import moment from 'moment-timezone';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import CircularProgress from '@material-ui/core/CircularProgress';
import ATable from 'components/Clinic/ClinicFinancials/ATable';
import PatientBadge from 'components/Patient/PatientBadge';

const ImmunizationInventorySection = ({ filter }: { filter: Filter }) => {
  const dispatch = useAppDispatch();
  const [immunizations, setImmunizations] = useState<PaidImmunizationDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Date range state - default to last 30 days
  const [fromDate, setFromDate] = useState(moment().subtract(30, 'days').unix());
  const [toDate, setToDate] = useState(moment().unix());

  // Fetch data when date range changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setImmunizations([]); // Clear data when loading starts
      try {
        const data = await dispatch(
          api.Stats.getPaidImmunizations({
            from: fromDate,
            to: toDate,
          }),
        );

        if (data) {
          setImmunizations(data);
        } else {
          setError('Failed to load immunization data');
        }
      } catch (err) {
        setError('Error loading immunization data: ' + (err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fromDate, toDate, dispatch]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const totalImmunizations = immunizations.length;
    const totalRevenue = immunizations.reduce(
      (sum, detail) => sum + (detail.invoice?.total || 0),
      0,
    );
    const withInvoices = immunizations.filter((detail) => detail.invoice).length;
    const withoutInvoices = totalImmunizations - withInvoices;

    return {
      totalImmunizations,
      totalRevenue: (totalRevenue / 100).toFixed(2), // Convert cents to dollars
      withInvoices,
      withoutInvoices,
    };
  }, [immunizations]);

  // Prepare table data
  const tableData = useMemo(() => {
    const headers = [
      'Date Given',
      'Invoice Date',
      'Patient',
      'Vaccine',
      'Prescription',
      'Invoice',
      'Amount',
    ];

    const rows = immunizations.map((detail, index) => {
      const imm = detail.immunization;
      const inv = detail.invoice;

      return [
        imm?.value?.given_date ? moment.unix(imm.value.given_date).format('MMM D, YYYY') : 'N/A',
        inv?.date ? moment.unix(inv.date).format('MMM D, YYYY') : 'N/A',
        <PatientBadge id={detail.patient_id!} goToProfile={true} key={`patient-${index}`} />,
        imm?.value?.name || 'N/A',
        imm?.value?.prescription || 'N/A',
        inv?.number || 'No invoice',
        inv?.total ? `$${(inv.total / 100).toFixed(2)}` : '-',
      ];
    });

    return { headers, rows };
  }, [immunizations]);

  return (
    <div className="immunization-inventory-section">
      <div className="grid gap-[var(--inner-gap)]">
        <DashboardBox
          title="Immunization Inventory"
          info="Paid immunizations across all clinics with invoice details"
          allow={ALLOW_ALL}
        >
          <div className="date-range-filter">
            <div className="flex gap-4 mb-4 items-center">
              <DefaultDatePicker
                name="from-date"
                label="From Date"
                value={fromDate}
                onChange={(e: any, value: number) => setFromDate(value)}
              />
              <DefaultDatePicker
                name="to-date"
                label="To Date"
                value={toDate}
                onChange={(e: any, value: number) => setToDate(value)}
              />
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center p-4">
                <CircularProgress />
              </div>
            )}

            {/* Error State */}
            {error && !loading && <div className="error-message p-4 text-red-500">{error}</div>}

            {/* Summary Stats - only show when we have data */}
            {!loading && !error && immunizations.length > 0 && (
              <>
                <div className="summary-stats grid grid-cols-4 gap-4 mb-4">
                  <div className="stat-box">
                    <div className="stat-label">Total Immunizations</div>
                    <div className="stat-value">{summary.totalImmunizations}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Total Revenue</div>
                    <div className="stat-value">${summary.totalRevenue}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">With Invoices</div>
                    <div className="stat-value">{summary.withInvoices}</div>
                  </div>
                  <div className="stat-box">
                    <div className="stat-label">Without Invoices</div>
                    <div className="stat-value">{summary.withoutInvoices}</div>
                  </div>
                </div>

                {/* Immunizations Table using ATable */}
                <ATable headers={tableData.headers} rows={tableData.rows} />
              </>
            )}

            {/* No data message */}
            {!loading && !error && immunizations.length === 0 && (
              <div className="no-data p-4 text-center text-gray-500">
                No immunizations found for the selected date range
              </div>
            )}
          </div>
        </DashboardBox>
      </div>
    </div>
  );
};

export default ImmunizationInventorySection;
